import LayoutHR from "layouts/layout-hr";
import React, { useState } from "react";
import { Dropdown } from "react-bootstrap";
import { useCanvasHandler } from "hooks/useCanvasHandler";
import Swal from "sweetalert2";
import MasterDataFormV2 from "../MasterDataFormV2";
import { TransactionsProvider, useTransactions } from "./context/TransactionsContext";
import { getDevelopmentBanner } from "utils/devNotice";
import { useNavigate } from "react-router-dom";
import { deleteRequest } from "services/api";

function TransactionsContent() {
  const { transactions, filterOptions, handleSearch, handleSearchChange, refetch, Pagination } =
    useTransactions();
  const navigate = useNavigate();

  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const { handleError, handleSuccessToast } = useCanvasHandler();

  const handleCreate = () => {
    setShowModal(true);
    setEditingItem(null);
  };

  const handleEdit = (item) => {
    setShowModal(true);
    setEditingItem(item);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await deleteRequest(`/ahr/transactions/${id}`);
        if (response.success) {
          handleSuccessToast(true, "Transaction deleted successfully");
          refetch();
        } else {
          handleError("Failed to delete transaction");
        }
      } catch (error) {
        handleError(error);
      }
    }
  };

  const handleFormSuccess = () => {
    setShowModal(false);
    setEditingItem(null);
    refetch();
    handleSuccessToast(true, "Transaction saved successfully");
  };

  const handleFormError = (error) => {
    handleError(error);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingItem(null);
  };

  const formFields = [
    {
      name: "amount",
      label: "Amount (IDR)",
      type: "number",
      required: true,
      isPrice: true,
    },
    {
      name: "type",
      label: "Type",
      type: "select",
      required: true,
      options: [
        { value: "income", label: "Income" },
        { value: "expense", label: "Expense" },
      ],
    },
    {
      name: "bank_id",
      label: "Bank UUID",
      type: "text",
      required: true,
    },
    {
      name: "description",
      label: "Description",
      type: "textarea",
    },
  ];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <LayoutHR>
      {getDevelopmentBanner()}
      <div className="container-xl">
        <div className="page-header d-print-none">
          <div className="row align-items-center">
            <div className="col-auto">
              <button className="btn btn-outline-secondary" onClick={() => navigate(-1)}>
                Back
              </button>
            </div>
            <div className="col">
              <h2 className="page-title">HR Transactions</h2>
              <div className="text-muted mt-1">Manage office income and expenses</div>
            </div>
            <div className="col-auto">
              <button className="btn btn-primary" onClick={handleCreate}>
                Add Transaction
              </button>
            </div>
          </div>
        </div>

        <div className="card mb-3">
          <div className="card-body">
            <div className="row">
              <div className="col-md-6">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search transactions..."
                  value={filterOptions.search}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  onKeyDown={handleSearch}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-vcenter table-hover">
                <thead>
                  <tr>
                    <th>No</th>
                    <th>Action</th>
                    <th>Type</th>
                    <th>Amount</th>
                    <th>Description</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.loading ? (
                    <tr>
                      <td colSpan="6" className="text-center">Loading...</td>
                    </tr>
                  ) : transactions.data?.length > 0 ? (
                    transactions.data.map((item, index) => (
                      <tr key={item.id}>
                        <td>{(filterOptions.currentPage - 1) * 10 + index + 1}</td>
                        <td>
                          <Dropdown>
                            <Dropdown.Toggle variant="default" size="sm">
                              Actions
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                              <Dropdown.Item onClick={() => handleEdit(item)}>Edit</Dropdown.Item>
                              <Dropdown.Item onClick={() => handleDelete(item.id)} className="text-danger">Delete</Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </td>
                        <td>
                          <span className={`badge bg-${item.type === 'income' ? 'success' : 'danger'}`}>
                            {item.type}
                          </span>
                        </td>
                        <td>{formatCurrency(item.amount)}</td>
                        <td>{item.description}</td>
                        <td>{new Date(item.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center">No transactions found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {transactions.pagination && <Pagination />}
          </div>
        </div>

        {showModal && (
          <div className="modal show d-block" tabIndex="-1">
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{editingItem ? "Edit Transaction" : "Create Transaction"}</h5>
                  <button type="button" className="btn-close" onClick={handleCloseModal}></button>
                </div>
                <div className="modal-body">
                  <MasterDataFormV2
                    apiUrl="/ahr/transactions"
                    initialData={editingItem}
                    fields={formFields}
                    onSuccess={handleFormSuccess}
                    onError={handleFormError}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </LayoutHR>
  );
}

export default function TransactionsPage() {
  return (
    <TransactionsProvider>
      <TransactionsContent />
    </TransactionsProvider>
  );
}
